import { useState, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { Message, ChatSession, ChatFile, ApiFileInfo, AgentActivity, MessageCitation, DifyHistoryMessage, ChatConnectedAccountPayload, CardBlock } from '@/types/chat';
import { useChatApi } from '@/hooks/useChatApi';
import { normalizeUrl } from '@/lib/urlNormalize';
import { cleanSocialMediaUrlsInText, resolveTikTokShortUrlsInText } from '@/lib/urlUtils';
// Note: isRateLimitError is deprecated, but kept for backward compatibility
// New code should use detectRateLimit from '@/lib/rateLimit/detection'
import { ChatApiService } from '@/services/chatApiService';
import { useRateLimit } from '@/hooks/useRateLimit';
import { PENDING_TASK_ID } from '../constants';
import { isDifyError, extractDifyErrorCode, ERROR_I18N_KEYS, ERROR_CODES } from '@/lib/dify/errorUtils';
import { extractStructuredAssistantResponse, type StructuredAssistantResponse } from '@/lib/assistantStructuredCards';
import { MessageHydrationAdapterV3 } from '@/lib/message-hydration';

// Error detection constants
const ERROR_DETECTION = {
  // Tool error patterns
  TOOL_ERROR_PATTERNS: {
    ITERATION_LIMIT: ['Maximum iteration limit', 'iteration limit reached'],
    TOOL_CALL_FAILURE: ['Cannot call tool', 'tool call failed'],
  },
  // Response completeness thresholds (in characters)
  LENGTH_THRESHOLDS: {
    EMPTY_RESPONSE: 20,
    VERY_LONG_RESPONSE: 2000,
    SHORT_RESPONSE: 150,
    MINIMAL_REMAINING_CONTENT: 200,
    ENDING_CHECK_CHARS: 100,
    SHORT_WITHOUT_PROPER_ENDING: 600,
    MEDIUM_RESPONSE: 1000,
    SINGLE_PARAGRAPH_THRESHOLD: 800,
  },
  // Structural analysis thresholds
  STRUCTURAL_THRESHOLDS: {
    MINIMUM_SECTIONS: 2,
    SUBSTANTIAL_FIRST_SECTION: 150,
    MINIMUM_SENTENCE_COUNT: 3,
    LINE_BREAKS_PER_HUNDRED_CHARS: 1,
  },
} as const;

type AgentThoughtEventPayload = {
  id?: string;
  position?: number;
  thought?: string;
  observation?: string;
  tool?: string;
  tool_labels?: Record<string, string> | null;
  tool_input?: unknown;
  created_at?: number;
  label?: string;
  status?: 'start' | 'running' | 'success' | 'error' | string;
  data?: any;
  [key: string]: any;
};

const deriveActivityType = (payload: AgentThoughtEventPayload): AgentActivity['type'] => {
  const label = typeof payload.label === 'string' ? payload.label.trim() : '';
  const toolFromPayload = typeof payload.tool === 'string' ? payload.tool.trim() : '';
  const toolFromData = typeof payload.data?.tool_name === 'string' ? payload.data.tool_name.trim() : '';
  const toolFromNestedData =
    typeof payload.data?.data?.tool_name === 'string'
      ? payload.data.data.tool_name.trim()
      : typeof payload.data?.data?.tool_call_name === 'string'
        ? payload.data.data.tool_call_name.trim()
        : '';
  const toolName = toolFromPayload || toolFromData || toolFromNestedData;

  // Explicit CALL labels represent concrete tool invocations.
  if (label.toUpperCase().startsWith('CALL ')) {
    return 'tool_call';
  }

  // Some nodes only expose the tool name in the label (e.g. "google-instagram-search")
  // without a dedicated tool_name field. When the label clearly matches one of our
  // known tools, treat this as a tool_call so it shows up as its own tool card.
  const normalizedLabel = label.toLowerCase();
  const knownToolLabelFragments = [
    'lead_gen_v9',
    'lead-gen-v9-google',
    'google_instagram_search',
    'google-instagram-search',
    'create_post',
    'post creation agent',
    'query_supabase', // Matches any version (v2, v3, v4, etc.)
    'query database',
    'scrape_ig_profile',
    'scrape_tiktok_profile',
    'link_captioner',
    'fetch_audience',
    'fetching_audience',
    'deep_statistics',
  ];
  if (normalizedLabel && knownToolLabelFragments.some(f => normalizedLabel.includes(f))) {
    return 'tool_call';
  }

  // If we have a concrete tool name, classify as a tool_call so the timeline
  // can surface structured tool usage steps.
  if (toolName.length > 0) {
    // Optionally separate search tools if the backend labels them clearly.
    const lower = toolName.toLowerCase();
    if (lower.includes('search') || lower.includes('brave')) {
      return 'search';
    }
    return 'tool_call';
  }

  // Otherwise treat as general thinking / planning.
  return 'thinking';
};

const extractActivityLabel = (payload: AgentThoughtEventPayload): string | undefined => {
  if (typeof payload.label === 'string' && payload.label.trim().length > 0) {
    return payload.label.trim();
  }
  if (typeof payload.data?.label === 'string' && payload.data.label.trim().length > 0) {
    return payload.data.label.trim();
  }
  if (payload.tool_labels && typeof payload.tool_labels === 'object') {
    const values = Object.values(payload.tool_labels).filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
    if (values.length > 0) {
      return values[0];
    }
  }

  if (payload.tool && payload.tool.trim().length > 0) {
    return payload.tool;
  }

  return undefined;
};

const upsertAgentActivity = (existing: AgentActivity[] | undefined, payload: AgentThoughtEventPayload): AgentActivity[] => {
  const activities = Array.isArray(existing) ? [...existing] : [];
  const activityId = payload.id || `agent-activity-${payload.position ?? Date.now()}`;
  const timestamp = payload.created_at ? payload.created_at * 1000 : Date.now();
  const type = deriveActivityType(payload);
  const label = extractActivityLabel(payload);
  const incomingStatus = typeof payload.status === 'string' ? payload.status : undefined;
  const normalizedStatus: AgentActivity['status'] = incomingStatus === 'error'
    ? 'error'
    : incomingStatus === 'success'
      ? 'success'
      : 'running';
  const baseData = {
    id: activityId,
    position: payload.position,
    thought: payload.thought,
    observation: payload.observation,
    tool_name: payload.tool,
    tool_input: payload.tool_input,
    raw: payload,
  };

  const existingIndex = activities.findIndex(activity => activity.data?.id === activityId);

  if (existingIndex === -1) {
    activities.push({
      type,
      status: normalizedStatus,
      label,
      timestamp,
      data: baseData,
    });
  } else {
    const current = activities[existingIndex];
    activities[existingIndex] = {
      ...current,
      type,
      label: label ?? current.label,
      status: current.status === 'error' ? current.status : normalizedStatus,
      timestamp,
      data: {
        ...current.data,
        ...baseData,
      },
    };
  }

  return activities;
};

const finalizeAgentActivities = (existing: AgentActivity[] | undefined, finalStatus: 'success' | 'error'): AgentActivity[] | undefined => {
  if (!existing || existing.length === 0) {
    return existing;
  }

  return existing.map(activity => {
    if (activity.status === 'error') {
      return activity;
    }

    if (finalStatus === 'error') {
      return { ...activity, status: 'error' };
    }

    if (activity.status === 'success') {
      return activity;
    }

    return { ...activity, status: 'success' };
  });
};

/**
 * Helper to detect tool errors in event data
 */
const detectToolError = (data: any): boolean => {
  // Check various possible locations for tool_responses
  const toolResponses =
    data.data?.outputs?.tool_responses ||
    data.outputs?.tool_responses ||
    data.data?.output?.tool_responses ||
    data.output?.tool_responses ||
    [];

  for (const toolResponse of toolResponses) {
    const response = toolResponse.tool_response || '';

    // Detect iteration limit errors
    const hasIterationLimitError = ERROR_DETECTION.TOOL_ERROR_PATTERNS.ITERATION_LIMIT.some(
      pattern => response.includes(pattern)
    );
    if (hasIterationLimitError) {
      return true;
    }

    // Detect other tool failures
    const hasToolCallFailure = ERROR_DETECTION.TOOL_ERROR_PATTERNS.TOOL_CALL_FAILURE.some(
      pattern => response.includes(pattern)
    );
    if (hasToolCallFailure) {
      return true;
    }
  }

  return false;
};

const CONTENT_POLICY_ERROR_PHRASE = "I'm sorry, but I cannot assist with that request.";
const CONTENT_POLICY_ERROR_REGEX = new RegExp(
  CONTENT_POLICY_ERROR_PHRASE.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
  'gi'
);

const detectContentPolicyError = (content: string): boolean => {
  if (!content || typeof content !== 'string') return false;
  return content.toLowerCase().includes(CONTENT_POLICY_ERROR_PHRASE.toLowerCase());
};

const stripContentPolicyError = (content: string): string => {
  if (!content || typeof content !== 'string') return content;
  return content.replace(CONTENT_POLICY_ERROR_REGEX, '').trim();
};

/**
 * Checks if response is empty or extremely short
 */
const isEmptyOrTooShort = (content: string, threshold: number): boolean => {
  return content.trim().length < threshold;
};

/**
 * Checks if response is suspiciously short (likely truncated)
 */
const isShortResponse = (content: string): boolean => {
  const trimmed = content.trim();
  return trimmed.length < ERROR_DETECTION.LENGTH_THRESHOLDS.SHORT_RESPONSE;
};

/**
 * Checks if response ends with proper punctuation or a valid URL
 * Language-agnostic: includes common sentence endings, CJK punctuation, and URLs
 */
const hasProperEnding = (content: string): boolean => {
  const trimmed = content.trim();
  const lastChars = trimmed.slice(-ERROR_DETECTION.LENGTH_THRESHOLDS.ENDING_CHECK_CHARS);
  // Check for proper punctuation
  if (/[.!?。！？][\s\n]*$|\.{3}[\s\n]*$/.test(lastChars)) {
    return true;
  }
  // Check if content ends with a URL (common for call-to-action messages)
  // URL pattern: matches http(s):// followed by domain and path
  if (/https?:\/\/\S+$/.test(trimmed)) {
    return true;
  }
  return false;
};

/**
 * Checks if content contains structured data indicators
 * Language-agnostic: looks for tables, code blocks, lists with data
 */
const hasStructuredData = (content: string): boolean => {
  const hasTable = /\|[^|]+\|[^|]+\|/.test(content);  // Tables with at least 2 columns
  const hasCodeBlock = /```/.test(content);  // Code blocks
  const hasListWithData = /^\s*[-*+]\s+.+:\s*.+$/m.test(content);  // Lists with key-value pairs
  const hasNumberedListWithData = /^\s*\d+\.\s+.+:\s*.+$/m.test(content);  // Numbered lists with data

  return hasTable || hasCodeBlock || hasListWithData || hasNumberedListWithData;
};

/**
 * Checks if response has structural imbalance (substantial first section but minimal remaining content)
 * This suggests planning text with no execution/results
 */
const hasStructuralImbalance = (content: string): boolean => {
  const trimmed = content.trim();
  const sections = trimmed.split(/\n\n+/);
  const { STRUCTURAL_THRESHOLDS, LENGTH_THRESHOLDS } = ERROR_DETECTION;

  if (sections.length < STRUCTURAL_THRESHOLDS.MINIMUM_SECTIONS) {
    return false;
  }

  const firstSection = sections[0];
  const remainingSections = sections.slice(1).join('\n\n');

  // Check if first section is substantial but remaining content is minimal
  const hasImbalance =
    firstSection.length > STRUCTURAL_THRESHOLDS.SUBSTANTIAL_FIRST_SECTION &&
    remainingSections.length < LENGTH_THRESHOLDS.MINIMAL_REMAINING_CONTENT;

  if (!hasImbalance) {
    return false;
  }

  // If there's structured data in remaining sections, it's likely complete
  return !hasStructuredData(remainingSections);
};

/**
 * Checks if response is narrative-only without actual data/results
 * Analyzes structure, sentence count, and line breaks
 */
const isNarrativeOnly = (content: string): boolean => {
  const trimmed = content.trim();
  const { LENGTH_THRESHOLDS, STRUCTURAL_THRESHOLDS } = ERROR_DETECTION;

  // Only check medium-length responses
  if (trimmed.length >= LENGTH_THRESHOLDS.MEDIUM_RESPONSE) {
    return false;
  }

  // If response has structured data, it's not narrative-only
  if (hasStructuredData(trimmed)) {
    return false;
  }

  const sentenceCount = (trimmed.match(/[.!?。！？]/g) || []).length;
  const lineBreakCount = (trimmed.match(/\n/g) || []).length;
  const hasMultipleSections = /\n\n.+\n\n/.test(trimmed);

  // Check if response has multiple sentences but minimal structure
  if (sentenceCount < STRUCTURAL_THRESHOLDS.MINIMUM_SENTENCE_COUNT) {
    return false;
  }

  // Single-paragraph narrative response under threshold
  if (!hasMultipleSections && trimmed.length < LENGTH_THRESHOLDS.SINGLE_PARAGRAPH_THRESHOLD) {
    return true;
  }

  // Dense narrative without sufficient line breaks
  const lineBreaksPerHundredChars = (lineBreakCount / trimmed.length) * 100;
  if (
    lineBreaksPerHundredChars < STRUCTURAL_THRESHOLDS.LINE_BREAKS_PER_HUNDRED_CHARS &&
    trimmed.length < LENGTH_THRESHOLDS.SINGLE_PARAGRAPH_THRESHOLD
  ) {
    return true;
  }

  return false;
};

/**
 * Checks if content contains structured card markers that weren't properly extracted
 * This indicates a card rendering failure
 */
const hasUnextractedCardMarkers = (content: string): boolean => {
  const trimmed = content.trim();

  // Check for <lema_cards> tags that weren't removed
  if (trimmed.includes('<lema_cards>') || trimmed.includes('</lema_cards>')) {
    return true;
  }

  // Check for JSON-like card structure at the start
  if (/^\s*\{\s*"cards"\s*:\s*\[/.test(trimmed)) {
    return true;
  }

  return false;
};

/**
 * Detects incomplete responses using language-agnostic structural analysis
 * Composes multiple pure helper functions for clarity and maintainability
 */
const detectIncompleteResponse = (responseContent: string): boolean => {
  // Handle null/undefined
  if (!responseContent) {
    return true;
  }

  const trimmedResponse = responseContent.trim();
  const { LENGTH_THRESHOLDS } = ERROR_DETECTION;

  // 1. Empty or extremely short responses are definitely errors
  if (isEmptyOrTooShort(responseContent, LENGTH_THRESHOLDS.EMPTY_RESPONSE)) {
    return true;
  }

  // 2. Check for unextracted card markers (card rendering failure)
  if (hasUnextractedCardMarkers(responseContent)) {
    return true;
  }

  // 3. Very long responses are likely complete, skip further checks
  if (trimmedResponse.length > LENGTH_THRESHOLDS.VERY_LONG_RESPONSE) {
    return false;
  }

  // 4. Check for suspiciously short responses (likely truncated mid-execution)
  if (isShortResponse(responseContent)) {
    return true;
  }

  // 5. Detect structural imbalance: response has sections but very little content
  if (hasStructuralImbalance(responseContent)) {
    return true;
  }

  // 6. Check if response ends with an incomplete sentence (no ending punctuation)
  // If response doesn't end properly and is relatively short, it's likely truncated
  if (
    !hasProperEnding(responseContent) &&
    trimmedResponse.length < LENGTH_THRESHOLDS.SHORT_WITHOUT_PROPER_ENDING
  ) {
    return true;
  }

  // 7. Check for responses that are all narrative/plan without actual data/results
  if (isNarrativeOnly(responseContent)) {
    return true;
  }

  return false;
};

export function useChatActions({
  // From useChatSessions
  userId,
  activeChat,
  chatSessionsRef,
  setChatSessions, // Needed for handleSaveEditAction
  updateChatSession,
  saveConversationsToCache,
  createNewChat,
  // From useChatMessages
  addOptimisticMessage,
  updateOptimisticMessage,
  removeMessagesAfterIndex,
  saveMessagesToCache,
  // From useChatFiles
  selectedFiles,
  setSelectedFiles,
  setIsUploading,
  // From useChatUI
  inputMessage,
  setInputMessage,
  replyingToMessage,
  cancelReply,
  // General
  chatApiService,
  handleRateLimitError,
  connectedAccountPayload,
}: {
  userId: string | null;
  activeChat: ChatSession | undefined;
  chatSessionsRef: React.RefObject<ChatSession[]>;
  setChatSessions: React.Dispatch<React.SetStateAction<ChatSession[]>>;
  updateChatSession: (chatId: string, updates: Partial<ChatSession>) => void;
  saveConversationsToCache: (sessions: ChatSession[]) => void;
  createNewChat: () => Promise<string | null>;
  addOptimisticMessage: (chatId: string, message: Message) => void;
  updateOptimisticMessage: (chatId: string, messageId: string, updates: Partial<Message>) => void;
  removeMessagesAfterIndex: (chatId: string, messageIndex: number) => void;
  saveMessagesToCache: (chatId: string, difyConvId: string | undefined, messages: Message[]) => void;
  selectedFiles: ChatFile[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<ChatFile[]>>;
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
  inputMessage: string;
  setInputMessage: (message: string) => void;
  replyingToMessage: Message | null;
  cancelReply: () => void;
  chatApiService: ChatApiService;
  handleRateLimitError?: (error: any, response?: Response) => Promise<boolean>;
  connectedAccountPayload?: ChatConnectedAccountPayload;
}) {
  const { t } = useTranslation();
  const chatApi = useChatApi(chatApiService);
  // Use handleRateLimitError from props if provided (shared instance from ChatProvider),
  // otherwise fall back to local instance (for backwards compatibility)
  const localRateLimit = useRateLimit();
  const handleRateLimitErrorFn = handleRateLimitError || localRateLimit.handleRateLimitError;

  const [isResponding, setIsResponding] = useState<boolean>(false);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);

  // Instantiate adapter once for the hook (not per stream)
  const adapter = useMemo(() => new MessageHydrationAdapterV3(userId ?? ''), [userId]);

  const getAssistantMessage = useCallback((chatId: string, messageId: string) => {
    const chat = chatSessionsRef.current?.find(session => session.id === chatId);
    if (!chat) return undefined;
    return chat.messages.find(msg => msg.id === messageId);
  }, [chatSessionsRef]);

  // Import rate limit utilities at module level to avoid async imports in stream processing
  const rateLimitDetection = (() => {
    try {
      // Use dynamic import but cache it
      let cached: { isRateLimitMessage: (msg: string) => boolean; createRateLimitError: any } | null = null;
      return {
        async isRateLimitMessage(msg: string) {
          if (!cached) {
            const mod = await import('@/lib/rateLimit/detection');
            cached = { isRateLimitMessage: mod.isRateLimitMessage, createRateLimitError: null };
          }
          return cached.isRateLimitMessage(msg);
        },
        async createRateLimitError(msg: string, info: any) {
          if (!cached || !cached.createRateLimitError) {
            const mod = await import('@/lib/rateLimit/types');
            cached = { ...cached, createRateLimitError: mod.createRateLimitError };
          }
          return cached.createRateLimitError(msg, info);
        },
      };
    } catch {
      return {
        isRateLimitMessage: () => false,
        createRateLimitError: () => new Error('Rate limit error'),
      };
    }
  })();

  /**
   * Helper to refresh conversation from Dify backend after stream errors
   * This recovers cases where the stream was interrupted but Dify completed the response
   */
  const refreshConversationAfterError = useCallback(async (
    chatId: string,
    assistantMessageId: string,
    difyConversationId: string | undefined,
    userMessageId?: string
  ) => {
    if (!difyConversationId || !userId) {
      return false;
    }

    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Dify Recovery] Refreshing conversation after error', {
          chatId,
          assistantMessageId,
          difyConversationId,
          userMessageId,
        });
      }

      // Fetch conversation history from Dify
      const history = await chatApi.fetchHistory(difyConversationId, userId);

      if (!history || !history.data || history.data.length === 0) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[Dify Recovery] No history found');
        }
        return false;
      }

      // Get current message to compare
      const currentMessage = getAssistantMessage(chatId, assistantMessageId);
      if (!currentMessage) {
        return false;
      }

      // Find matching message in Dify history
      // Dify history format: { id, query, answer, created_at, ... }
      let matchedDifyMessage: DifyHistoryMessage | undefined = undefined;

      if (currentMessage.difyMessageId) {
        // Match by difyMessageId (corresponds to msg.id in Dify response)
        matchedDifyMessage = history.data.find(
          (msg: DifyHistoryMessage) => msg.id === currentMessage.difyMessageId && msg.answer
        );
      } else if (userMessageId) {
        // For orphaned user messages: Find the response by matching the user message's difyMessageId
        const currentChat = chatSessionsRef.current?.find((c: ChatSession) => c.id === chatId);
        const userMsg = currentChat?.messages.find((m: Message) => m.id === userMessageId);

        if (userMsg?.difyMessageId) {
          // Find the Dify message with this user query
          matchedDifyMessage = history.data.find(
            (msg: DifyHistoryMessage) => msg.id === userMsg.difyMessageId && msg.answer
          );

          if (process.env.NODE_ENV === 'development' && matchedDifyMessage) {
            console.log('[Dify Recovery] Matched by user message difyMessageId', {
              userMessageId,
              userDifyMessageId: userMsg.difyMessageId,
              foundAnswer: !!matchedDifyMessage.answer,
            });
          }
        }

        // Fallback: If user message doesn't have difyMessageId, take the last message with an answer
        if (!matchedDifyMessage) {
          matchedDifyMessage = [...history.data]
            .reverse()
            .find((msg: DifyHistoryMessage) => msg.answer);
        }
      } else {
        // No difyMessageId and no userMessageId - take the last message with an answer
        // (since we just sent a message, the last message should be ours)
        matchedDifyMessage = [...history.data]
          .reverse()
          .find((msg: DifyHistoryMessage) => msg.answer);
      }

      if (!matchedDifyMessage || !matchedDifyMessage.answer) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[Dify Recovery] No matching message found in history');
        }
        return false;
      }

      // Check if the history message has more content than our partial stream
      const currentContentLength = currentMessage.content.trim().length;
      const structuredAnswer = extractStructuredAssistantResponse(matchedDifyMessage.answer, { final: true });
      const historyContentLength = structuredAnswer.content.trim().length;

      if (historyContentLength > currentContentLength) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[Dify Recovery] Found complete message in history, updating', {
            currentLength: currentContentLength,
            historyLength: historyContentLength,
            difyMessageId: matchedDifyMessage.id,
          });
        }

        // Update message with the complete content from history
        const finalizedActivities = finalizeAgentActivities(
          currentMessage.agentActivity,
          'success'
        );

        updateOptimisticMessage(chatId, assistantMessageId, {
          content: structuredAnswer.content,
          difyMessageId: matchedDifyMessage.id,
          isThinking: false,
          agentActivity: finalizedActivities,
          citations: currentMessage.citations || [],
          hasCitations: currentMessage.hasCitations || false,
          cards: structuredAnswer.cards.length > 0 ? structuredAnswer.cards : undefined,
          cardBlocks: structuredAnswer.cardBlocks.length > 0 ? structuredAnswer.cardBlocks : undefined,
          // Clear all error flags since we recovered the complete message
          errorMessage: undefined,
          isDifyAgentError: false,
          isRateLimitError: false,
          isIncomplete: false,
        });

        // Save to cache
        const finalChatState = chatSessionsRef.current?.find((c: ChatSession) => c.id === chatId);
        if (finalChatState) {
          saveMessagesToCache(chatId, difyConversationId, finalChatState.messages);
          saveConversationsToCache(chatSessionsRef.current!.map((s: ChatSession) => s.id === chatId ? { ...s, updatedAt: new Date() } : s));
        }

        return true;
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('[Dify Recovery] History message not longer than current', {
            currentLength: currentContentLength,
            historyLength: historyContentLength,
          });
        }
        return false;
      }
    } catch (error) {
      console.error('[Dify Recovery] Error refreshing conversation:', error);
      return false;
    }
  }, [userId, chatApi, getAssistantMessage, updateOptimisticMessage, chatSessionsRef, saveMessagesToCache, saveConversationsToCache]);

  /**
   * Detects if the response has tool errors
   */
  const detectResponseErrors = useCallback((
    detectedToolError: boolean,
    streamCompletedProperly: boolean,
    structuredAnswer: StructuredAssistantResponse,
    workflowData?: unknown
  ) => {
    // Check tracked tool errors and workflow data
    let hasToolError = detectedToolError;
    if (!hasToolError && workflowData) {
      hasToolError = detectToolError(workflowData);
    }

    // Check for incomplete responses only if stream didn't complete properly
    // Skip incomplete detection if response has structured cards
    const hasStructuredContent = structuredAnswer.hasStructuredCards && structuredAnswer.cards.length > 0;
    const isIncomplete = streamCompletedProperly
      ? false
      : (hasStructuredContent ? false : detectIncompleteResponse(structuredAnswer.content));

    return { hasToolError, isIncomplete, hasError: hasToolError || isIncomplete };
  }, []);

  /**
   * Builds the base message update object
   */
  const buildMessageUpdate = useCallback((
    structuredAnswer: StructuredAssistantResponse,
    receivedDifyMessageId: string | undefined,
    currentMessage: Message | undefined,
    hasError: boolean
  ): Partial<Message> => {
    const finalizedActivities = finalizeAgentActivities(
      currentMessage?.agentActivity,
      hasError ? 'error' : 'success'
    );

    const finalCitations = currentMessage?.citations || [];
    const finalCards = currentMessage?.cards && currentMessage.cards.length > 0
      ? currentMessage.cards
      : structuredAnswer.cards.length > 0
        ? structuredAnswer.cards
        : undefined;
    const finalCardBlocks = currentMessage?.cardBlocks && currentMessage.cardBlocks.length > 0
      ? currentMessage.cardBlocks
      : structuredAnswer.cardBlocks.length > 0
        ? structuredAnswer.cardBlocks
        : undefined;

    return {
      content: structuredAnswer.content,
      difyMessageId: receivedDifyMessageId,
      isThinking: false,
      agentActivity: finalizedActivities,
      citations: finalCitations,
      hasCitations: finalCitations.length > 0,
      cards: finalCards,
      cardBlocks: finalCardBlocks,
      isRateLimitError: false,
    };
  }, []);

  /**
   * Helper to finalize message with error detection and updates
   * Eliminates duplication between workflow_finished handler and finally block
   * @param streamCompletedProperly - true if workflow_finished was received, false if stream was interrupted
   */
  const finalizeMessageWithErrorDetection = useCallback((
    chatId: string,
    assistantMessageId: string,
    finalContent: string,
    receivedDifyMessageId: string | undefined,
    detectedToolError: boolean,
    streamCompletedProperly: boolean,
    workflowData?: unknown,
    isContentPolicyError?: boolean,
    isStreamError?: boolean
  ) => {
    const currentMessage = getAssistantMessage(chatId, assistantMessageId);
    const structuredAnswer = extractStructuredAssistantResponse(finalContent, { final: true });

    const { hasToolError, isIncomplete, hasError } = detectResponseErrors(
      detectedToolError,
      streamCompletedProperly,
      structuredAnswer,
      workflowData
    );

    if (process.env.NODE_ENV === 'development' && hasError) {
      console.log('[Dify Error Detection] Error detected', {
        hasToolError,
        isIncomplete,
        streamCompletedProperly,
        isStreamError
      });
    }

    const messageUpdate = buildMessageUpdate(
      structuredAnswer,
      receivedDifyMessageId,
      currentMessage,
      hasError || !!isStreamError
    );

    if (hasError || isContentPolicyError || isStreamError) {
      updateOptimisticMessage(chatId, assistantMessageId, {
        ...messageUpdate,
        errorMessage: hasToolError ? t(ERROR_I18N_KEYS.CHAT.DIFY_AGENT_ERROR) : undefined,
        errorCode: hasToolError ? ERROR_CODES.CHAT.DIFY_AGENT_ERROR : undefined,
        isDifyAgentError: hasToolError,
        isIncomplete,
        isContentPolicyError: isContentPolicyError || false,
        isStreamError: isStreamError || false,
      });
    } else {
      updateOptimisticMessage(chatId, assistantMessageId, {
        ...messageUpdate,
        isDifyAgentError: false,
        isIncomplete: false,
        isContentPolicyError: false,
        isStreamError: false,
      });
    }
  }, [getAssistantMessage, updateOptimisticMessage, detectResponseErrors, buildMessageUpdate, t]);

  const processStream = useCallback(async (
    reader: ReadableStreamDefaultReader<Uint8Array>,
    chatId: string,
    assistantMessageId: string,
    initialConversationId: string | undefined
  ) => {
    // ... (same implementation as before)
    const decoder = new TextDecoder();
    let fullResponse = "";
    let receivedConversationId = initialConversationId || "";
    let receivedDifyMessageId = "";
    let incompleteLine = "";
    let currentStreamTaskId: string | null = null;
    const streamTimeout = setTimeout(() => reader.cancel(), 300000);
    let streamFinished = false;
    let detectedToolError = false;
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done || !value) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = (incompleteLine + chunk).split("\n");
        incompleteLine = lines.pop() || "";
        for (const line of lines) {
          const dataMatch = line.match(/^data:\s*(.*)$/);
          if (dataMatch) {
            const jsonString = (dataMatch[1] || "").trim();
            if (jsonString) {
              try {
                const data = JSON.parse(jsonString);
                if (data.conversation_id && !receivedConversationId) {
                  receivedConversationId = data.conversation_id;
                  updateChatSession(chatId, { difyConversationId: receivedConversationId });
                  // Persist immediately so a page refresh can recover the answer via fetchHistory.
                  // chatSessionsRef.current won't yet reflect the updateChatSession call above
                  // (that's async), so we apply the update manually before saving.
                  saveConversationsToCache(chatSessionsRef.current.map(s =>
                    s.id === chatId ? { ...s, difyConversationId: receivedConversationId } : s
                  ));
                }
                if (data.task_id && (!currentStreamTaskId || currentStreamTaskId === PENDING_TASK_ID)) {
                  currentStreamTaskId = data.task_id;
                  setCurrentTaskId(prev => prev === PENDING_TASK_ID ? currentStreamTaskId : prev);
                }
                if (data.event === "agent_thought") {
                  const currentMessage = getAssistantMessage(chatId, assistantMessageId);
                  const updatedActivities = upsertAgentActivity(currentMessage?.agentActivity, data as AgentThoughtEventPayload);
                  updateOptimisticMessage(chatId, assistantMessageId, { agentActivity: updatedActivities, isThinking: true });
                  continue;
                }
                // Extract citations from retrieval/document events
                if (data.event === "retrieval" || data.event === "document" || data.retrieval || data.metadata?.citations) {
                  const citations: MessageCitation[] = [];

                  // Handle retrieval.documents format
                  if (data.retrieval?.documents && Array.isArray(data.retrieval.documents)) {
                    data.retrieval.documents.forEach((doc: any, idx: number) => {
                      const url = doc.url || doc.source || doc.link;
                      if (url) {
                        citations.push({
                          id: doc.id || `cite-${assistantMessageId}-${idx}`,
                          url: normalizeUrl(url),
                          title: doc.title || doc.name || doc.filename || 'Untitled',
                          index: idx + 1,
                          snippet: doc.content || doc.snippet || doc.text,
                        });
                      }
                    });
                  }

                  // Handle metadata.citations format
                  if (data.metadata?.citations && Array.isArray(data.metadata.citations)) {
                    data.metadata.citations.forEach((cite: any, idx: number) => {
                      const url = cite.url || cite.source || cite.link;
                      if (url) {
                        citations.push({
                          id: cite.id || `cite-${assistantMessageId}-${idx}`,
                          url: normalizeUrl(url),
                          title: cite.title || cite.name || 'Untitled',
                          index: idx + 1,
                          snippet: cite.snippet || cite.content || cite.text,
                        });
                      }
                    });
                  }

                  // Handle direct citations array
                  if (Array.isArray(data.citations)) {
                    data.citations.forEach((cite: any, idx: number) => {
                      const url = cite.url || cite.source || cite.link;
                      if (url) {
                        citations.push({
                          id: cite.id || `cite-${assistantMessageId}-${idx}`,
                          url: normalizeUrl(url),
                          title: cite.title || cite.name || 'Untitled',
                          index: idx + 1,
                          snippet: cite.snippet || cite.content || cite.text,
                        });
                      }
                    });
                  }

                  // Update message with citations if any were found
                  if (citations.length > 0) {
                    const currentMessage = getAssistantMessage(chatId, assistantMessageId);
                    const existingCitations = currentMessage?.citations || [];
                    // Merge with existing citations, avoiding duplicates by URL
                    const existingUrls = new Set(existingCitations.map(c => normalizeUrl(c.url)));
                    const newCitations = citations.filter(c => !existingUrls.has(normalizeUrl(c.url)));
                    const mergedCitations = [...existingCitations, ...newCitations];
                    
                    // Debug logging for canvas trigger (development only)
                    if (process.env.NODE_ENV === 'development') {
                      console.log('[Canvas Debug] Citations detected in stream', {
                        messageId: assistantMessageId,
                        newCitationsCount: newCitations.length,
                        existingCitationsCount: existingCitations.length,
                        mergedCitationsCount: mergedCitations.length,
                        citationIds: mergedCitations.map(c => c.id),
                        citationUrls: mergedCitations.map(c => c.url),
                      });
                    }
                    
                    updateOptimisticMessage(chatId, assistantMessageId, {
                      citations: mergedCitations,
                      hasCitations: mergedCitations.length > 0,
                    });
                    
                    if (process.env.NODE_ENV === 'development') {
                      console.log('[Canvas Debug] Message updated with citations, will trigger auto-open check', {
                        messageId: assistantMessageId,
                        totalCitations: mergedCitations.length,
                      });
                    }
                  }
                }
                if (data.event === "agent_log") {
                  // Check for tool errors in agent_log event
                  const toolError = detectToolError(data);
                  if (toolError && !detectedToolError) {
                    detectedToolError = true;
                  }

                  const currentMessage = getAssistantMessage(chatId, assistantMessageId);
                  const agentData = data.data || {};
                  const nestedAgentData = agentData.data || {};
                  const logPayload: AgentThoughtEventPayload = {
                    id: agentData.id || data.id,
                    position: agentData.position ?? nestedAgentData.position,
                    thought:
                      agentData.output ||
                      nestedAgentData.output ||
                      agentData.thought ||
                      nestedAgentData.thought,
                    observation: agentData.observation || nestedAgentData.observation,
                    tool:
                      agentData.tool_name ||
                      agentData.tool_call_name ||
                      nestedAgentData.tool_name ||
                      nestedAgentData.tool_call_name,
                    tool_labels: null,
                    tool_input:
                      agentData.tool_input ||
                      agentData.tool_call_input ||
                      nestedAgentData.tool_input ||
                      nestedAgentData.tool_call_input,
                    created_at: data.created_at,
                    label: agentData.label ?? data.label,
                    status: agentData.status ?? data.status,
                    data: agentData,
                  };
                  const updatedActivities = upsertAgentActivity(currentMessage?.agentActivity, logPayload);
                  updateOptimisticMessage(chatId, assistantMessageId, { agentActivity: updatedActivities, isThinking: true });
                  continue;
                }
                if (data.event === "workflow_started") {
                  const currentMessage = getAssistantMessage(chatId, assistantMessageId);
                  const wfPayload: AgentThoughtEventPayload = {
                    id: data.task_id || data.id,
                    position: undefined,
                    thought: undefined,
                    observation: undefined,
                    tool: undefined,
                    tool_labels: null,
                    tool_input: undefined,
                    created_at: data.created_at,
                    label: typeof data.data?.title === "string" ? data.data.title : undefined,
                    status: data.status,
                    data: data.data,
                  };
                  const updatedActivities = upsertAgentActivity(currentMessage?.agentActivity, wfPayload);
                  updateOptimisticMessage(chatId, assistantMessageId, { agentActivity: updatedActivities, isThinking: true });
                  continue;
                }
                if (data.event === "node_started" || data.event === "node_finished") {
                  // Check for tool errors in node_finished event
                  if (data.event === "node_finished") {
                    const toolError = detectToolError(data);
                    if (toolError && !detectedToolError) {
                      detectedToolError = true;
                    }
                  }

                  const currentMessage = getAssistantMessage(chatId, assistantMessageId);
                  const nodeData = data.data || {};
                  const nodeType = nodeData.node_type || nodeData.type;
                  const nodeTitle = nodeData.node_title || nodeData.title;
                  const isToolNode = typeof nodeType === "string" && nodeType.toLowerCase() === "tool";

                  // For node_finished events, Dify sometimes omits an explicit status.
                  // In that case we treat the node as success so tool-call steps can
                  // complete independently of the final assistant message.
                  const rawStatusFromEvent = data.status;
                  const rawStatusFromNode = nodeData.status;
                  const resolvedStatus = data.event === "node_finished"
                    ? (rawStatusFromEvent || rawStatusFromNode || "success")
                    : (rawStatusFromEvent || rawStatusFromNode);

                  const nodePayload: AgentThoughtEventPayload = {
                    id: nodeData.id || data.id,
                    position: nodeData.index ?? nodeData.position,
                    thought: nodeData.output || nodeData.data?.output,
                    observation: nodeData.observation,
                    tool: isToolNode ? (nodeData.tool_name || nodeData.tool_call_name || nodeTitle) : undefined,
                    tool_labels: null,
                    tool_input: nodeData.tool_input || nodeData.tool_call_input,
                    created_at: data.created_at,
                    label: typeof nodeTitle === "string" ? nodeTitle : data.title,
                    status: resolvedStatus,
                    data: nodeData,
                  };

                  const updatedActivities = upsertAgentActivity(currentMessage?.agentActivity, nodePayload);
                  updateOptimisticMessage(chatId, assistantMessageId, { agentActivity: updatedActivities, isThinking: true });
                  continue;
                }
                if (data.event === "message" || data.event === "agent_message") {
                  const chunk =
                    (typeof data.delta === "string" && data.delta) ||
                    (typeof data.answer === "string" && data.answer) ||
                    (typeof data.text === "string" && data.text) ||
                    (typeof data.outputs?.delta === "string" && data.outputs.delta) ||
                    (typeof data.outputs?.answer === "string" && data.outputs.answer) ||
                    (typeof data.outputs?.text === "string" && data.outputs.text) ||
                    "";
                  if (chunk) fullResponse += chunk;

                  // Check for content policy error
                  const hasContentPolicyError = detectContentPolicyError(fullResponse);
                  const sanitizedContent = hasContentPolicyError
                    ? stripContentPolicyError(fullResponse)
                    : fullResponse;

                  receivedDifyMessageId = data.message_id || receivedDifyMessageId;
                  updateOptimisticMessage(chatId, assistantMessageId, {
                    content: sanitizedContent,
                    difyMessageId: receivedDifyMessageId,
                    isContentPolicyError: hasContentPolicyError,
                  });
                } else if (data.event === "lema_cards") {
                  // Progressive card block streaming
                  const incomingBlock = {
                    cards: data.cards,
                    position: data.position ?? 0
                  };

                  const currentMessage = getAssistantMessage(chatId, assistantMessageId);
                  if (!currentMessage) continue;

                  // Use adapter to merge card blocks
                  const merged = adapter.merge(currentMessage, { cardBlocks: [incomingBlock] });

                  updateOptimisticMessage(chatId, assistantMessageId, {
                    cardBlocks: merged.cardBlocks,
                    cards: merged.cards,
                  });
                } else if (data.event === "workflow_finished") {
                  const workflowAnswer =
                    (typeof data.data?.outputs?.answer === "string" && data.data.outputs.answer) ||
                    (typeof data.outputs?.answer === "string" && data.outputs.answer) ||
                    (typeof data.answer === "string" && data.answer) ||
                    (typeof data.data?.outputs?.text === "string" && data.data.outputs.text) ||
                    (typeof data.outputs?.text === "string" && data.outputs.text) ||
                    (typeof data.text === "string" && data.text) ||
                    "";
                  const rawFinalContent = workflowAnswer || fullResponse;

                  // Check for content policy error before passing to finalization
                  const hasContentPolicyError = detectContentPolicyError(rawFinalContent);
                  const sanitizedRawContent = hasContentPolicyError
                    ? stripContentPolicyError(rawFinalContent)
                    : rawFinalContent;

                  receivedDifyMessageId = data.message_id || receivedDifyMessageId;

                  // Debug logging for short responses
                  if (process.env.NODE_ENV === 'development' && sanitizedRawContent.trim().length < 50) {
                    console.log('[Dify Error Detection] Short/empty response detected:', {
                      workflowAnswer,
                      fullResponse,
                      rawFinalContent,
                      sanitizedRawContent,
                      contentLength: sanitizedRawContent.length,
                      trimmedLength: sanitizedRawContent.trim().length,
                      detectedToolError,
                      workflowData: data
                    });
                  }

                  // Finalize message with error detection
                  // Stream completed properly since we received workflow_finished event
                  // We pass the raw sanitizedRawContent so finalizeMessageWithErrorDetection can extract cards
                  finalizeMessageWithErrorDetection(
                    chatId,
                    assistantMessageId,
                    sanitizedRawContent,
                    receivedDifyMessageId,
                    detectedToolError,
                    true, // streamCompletedProperly
                    data,
                    hasContentPolicyError
                  );
                  const finalChatState = chatSessionsRef.current?.find(c => c.id === chatId);
                  if (finalChatState && receivedConversationId) {
                    saveMessagesToCache(chatId, receivedConversationId, finalChatState.messages);
                    saveConversationsToCache(chatSessionsRef.current!.map(s => s.id === chatId ? { ...s, updatedAt: new Date() } : s));
                  }
                  setCurrentTaskId(prev => prev === currentStreamTaskId ? null : prev);
                  streamFinished = true;
                } else if (data.event === "message_end") {
                  const finalMessageId = data.message_id || receivedDifyMessageId;
                  const currentMessage = getAssistantMessage(chatId, assistantMessageId);

                  // Check for content policy error
                  const hasContentPolicyError = detectContentPolicyError(fullResponse);
                  const sanitizedContent = hasContentPolicyError
                    ? stripContentPolicyError(fullResponse)
                    : fullResponse;

                  // Extract structured cards from final content
                  const structuredAnswer = extractStructuredAssistantResponse(sanitizedContent, { final: true });

                  // Detect if response is incomplete (e.g., agent timeout with no content)
                  const { hasToolError, isIncomplete, hasError } = detectResponseErrors(
                    detectedToolError,
                    true, // message_end means stream completed (but might still be incomplete/empty)
                    structuredAnswer,
                    undefined
                  );

                  // Determine final activity status based on error detection
                  const activityStatus = hasError ? 'error' : 'success';
                  const finalizedActivities = finalizeAgentActivities(currentMessage?.agentActivity, activityStatus);

                  // Preserve existing citations when finalizing
                  const finalCitations = currentMessage?.citations || [];

                  // Use adapter to merge final structured content
                  const final = adapter.merge(currentMessage ?? {
                    id: assistantMessageId,
                    role: 'assistant',
                    content: '',
                    timestamp: new Date()
                  }, {
                    content: structuredAnswer.content,
                    cards: structuredAnswer.cards.length > 0 ? structuredAnswer.cards : undefined,
                    cardBlocks: structuredAnswer.cardBlocks.length > 0 ? structuredAnswer.cardBlocks : undefined,
                  });

                  updateOptimisticMessage(chatId, assistantMessageId, {
                    difyMessageId: finalMessageId,
                    isThinking: false,
                    content: final.content,
                    agentActivity: finalizedActivities,
                    citations: finalCitations,
                    hasCitations: finalCitations.length > 0,
                    cards: final.cards,
                    cardBlocks: final.cardBlocks,
                    isRateLimitError: false,
                    isContentPolicyError: hasContentPolicyError,
                    isDifyAgentError: hasToolError,
                    isIncomplete,
                    errorMessage: hasToolError ? t(ERROR_I18N_KEYS.CHAT.DIFY_AGENT_ERROR) : undefined,
                    errorCode: hasToolError ? ERROR_CODES.CHAT.DIFY_AGENT_ERROR : undefined,
                  });
                  const finalChatState = chatSessionsRef.current?.find(c => c.id === chatId);
                  if (finalChatState && receivedConversationId) {
                    saveMessagesToCache(chatId, receivedConversationId, finalChatState.messages);
                    saveConversationsToCache(chatSessionsRef.current!.map(s => s.id === chatId ? { ...s, updatedAt: new Date() } : s));
                  }
                  setCurrentTaskId(prev => prev === currentStreamTaskId ? null : prev);
                  streamFinished = true;
                } else if (data.event === "error") {
                  const currentMessage = getAssistantMessage(chatId, assistantMessageId);
                  const erroredActivities = finalizeAgentActivities(currentMessage?.agentActivity, 'error');
                  const errorMessage = data.message || '';

                  // Use centralized rate limit detection
                  const isRateLimit = await rateLimitDetection.isRateLimitMessage(errorMessage);

                  // Check if it's a Dify error
                  const isDify = isDifyError(errorMessage);
                  const difyStatusCode = extractDifyErrorCode(errorMessage);

                  if (isRateLimit) {
                    updateOptimisticMessage(chatId, assistantMessageId, {
                      content: `${fullResponse}\n\n*Stream Error: ${errorMessage}*`,
                      agentActivity: erroredActivities,
                      isRateLimitError: true
                    });

                    // Trigger the rate limit dialog
                    if (handleRateLimitErrorFn) {
                      try {
                        const rateLimitError = await rateLimitDetection.createRateLimitError(
                          errorMessage,
                          { exceeded: true, isMonthlyLimit: errorMessage.toLowerCase().includes('monthly limit') }
                        );
                        await handleRateLimitErrorFn(rateLimitError);
                      } catch (err) {
                        console.error('[useChatActions] Failed to handle stream rate limit error:', err);
                      }
                    }
                  } else if (isDify && difyStatusCode) {
                    // Display Dify errors with ErrorCard
                    updateOptimisticMessage(chatId, assistantMessageId, {
                      content: fullResponse,
                      agentActivity: erroredActivities,
                      isDifyAgentError: true,
                      errorMessage: errorMessage,
                      errorCode: ERROR_CODES.CHAT.DIFY_AGENT_ERROR,
                    });
                  } else {
                    // Generic error handling
                    updateOptimisticMessage(chatId, assistantMessageId, {
                      content: `${fullResponse}\n\n*Stream Error: ${errorMessage}*`,
                      agentActivity: erroredActivities
                    });
                  }

                  streamFinished = true;
                } else if (
                  (typeof data.delta === "string" && data.delta) ||
                  (typeof data.answer === "string" && data.answer) ||
                  (typeof data.text === "string" && data.text)
                ) {
                  const extra = (data.delta || data.answer || data.text) as string;
                  fullResponse += extra;
                  receivedDifyMessageId = data.message_id || receivedDifyMessageId;

                  // Extract cards and clean content during streaming to hide incomplete <lema_cards> tags
                  const extracted = extractStructuredAssistantResponse(fullResponse, { final: false });
                  updateOptimisticMessage(chatId, assistantMessageId, {
                    content: extracted.content,
                    cards: extracted.cards,
                    cardBlocks: extracted.cardBlocks,
                    difyMessageId: receivedDifyMessageId
                  });
                }
              } catch (e) { }
            }
          }
        }
      }
    } catch (readError) {
      // Stream connection error (TCP failures, network issues)
      // Use unified error detection with stream error flag
      // We pass the raw fullResponse so finalizeMessageWithErrorDetection can extract cards
      finalizeMessageWithErrorDetection(
        chatId,
        assistantMessageId,
        fullResponse,
        receivedDifyMessageId || undefined,
        detectedToolError,
        false, // streamCompletedProperly
        undefined, // workflowData
        false, // isContentPolicyError
        true // isStreamError
      );

      // Mark as finished so the finally block doesn't run redundant error detection
      streamFinished = true;
    } finally {
      clearTimeout(streamTimeout);
      setCurrentTaskId(prev => prev === currentStreamTaskId ? null : prev);
      if (!streamFinished) {
        // Stream ended without workflow_finished event - check for errors
        if (process.env.NODE_ENV === 'development') {
          console.log('[Dify Error Detection] Stream ended without workflow_finished:', {
            fullResponse,
            fullResponseLength: fullResponse.length,
            detectedToolError
          });
        }

        // Finalize message with error detection
        // Stream did NOT complete properly (no workflow_finished event)
        // We pass the raw fullResponse so finalizeMessageWithErrorDetection can extract cards
        finalizeMessageWithErrorDetection(
          chatId,
          assistantMessageId,
          fullResponse,
          receivedDifyMessageId || undefined,
          detectedToolError,
          false // streamCompletedProperly
        );

        // After detecting an incomplete response, try to refresh from Dify backend
        // This handles cases where the user closed the tab but Dify finished the response
        if (receivedConversationId) {
          // Run refresh in background - don't block on this
          refreshConversationAfterError(chatId, assistantMessageId, receivedConversationId)
            .catch(err => console.error('[Dify Recovery] Background refresh failed:', err));
        }
      }
      setIsResponding(false);
    }
  }, [updateChatSession, updateOptimisticMessage, saveMessagesToCache, saveConversationsToCache, chatSessionsRef, getAssistantMessage, finalizeMessageWithErrorDetection, t]);

  const sendMessage = useCallback(async (messageOverride?: string) => {
    // Check responding state FIRST to prevent race condition
    if (isResponding) {
      // eslint-disable-next-line no-console
      console.log('[sendMessage] early return: already responding');
      return;
    }

    // Set responding state immediately to prevent race condition
    setIsResponding(true);

    try {
      const rawInputMessage = messageOverride || inputMessage;

      // Step 1: Resolve TikTok short URLs to full URLs
      const resolvedMessage = await resolveTikTokShortUrlsInText(rawInputMessage);

      // Step 2: Clean social media URLs (remove tracking parameters)
      const currentInputMessage = cleanSocialMediaUrlsInText(resolvedMessage);

      // Debug logging (development only)
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('[Canvas Debug] 🚀 sendMessage invoked', {
          hasOverride: typeof messageOverride === 'string',
          overrideLength: messageOverride?.length ?? 0,
          currentInputLength: currentInputMessage.length,
          hasUserId: !!userId,
          selectedFilesCount: selectedFiles.length,
          isResponding,
          activeChatId: activeChat?.id,
        });
      }

      if ((!currentInputMessage.trim() && selectedFiles.length === 0) || !userId) {
        // eslint-disable-next-line no-console
        console.log('[sendMessage] early return: empty message or missing userId', {
          hasContent: !!currentInputMessage.trim(),
          selectedFilesCount: selectedFiles.length,
          hasUserId: !!userId,
        });
        return;
      }

      let currentActiveChatId = activeChat?.id;
      if (!currentActiveChatId) {
        // eslint-disable-next-line no-console
        console.log('[sendMessage] no activeChat, creating new chat');
        const newChatId = await createNewChat();
        // eslint-disable-next-line no-console
        console.log('[sendMessage] createNewChat result', { newChatId });
        if (!newChatId) {
          // eslint-disable-next-line no-console
          console.log('[sendMessage] early return: createNewChat returned null');
          return;
        }
        currentActiveChatId = newChatId;
      }

      const replyToId = replyingToMessage?.id;
      cancelReply();
      let initialChat = chatSessionsRef.current?.find(chat => chat.id === currentActiveChatId);
      if (!initialChat) {
        // eslint-disable-next-line no-console
        console.error('[sendMessage] could not find chat by id, creating fallback session', {
          currentActiveChatId,
          chatCount: chatSessionsRef.current?.length ?? 0,
        });

        if (!userId) {
          // Should not happen due to earlier guard, but bail out safely
          return;
        }

        const now = new Date();
        initialChat = {
          id: currentActiveChatId,
          difyConversationId: "",
          title: t('new_chat_title'),
          messages: [],
          createdAt: now,
          updatedAt: now,
          files: [],
          userId,
        };

        const updatedSessions = [initialChat, ...(chatSessionsRef.current || [])];
        setChatSessions(updatedSessions);
        saveConversationsToCache(updatedSessions);
      }
    setCurrentTaskId(PENDING_TASK_ID);
    const filesAddedThisTurn = [...selectedFiles];
    if (filesAddedThisTurn.length > 0) {
      updateChatSession(currentActiveChatId, { files: [...initialChat.files, ...filesAddedThisTurn] });
    }
    const messageFileIds = filesAddedThisTurn.map(f => f.id);
    const userMessage: Message = { id: `msg-${uuidv4()}`, role: "user", content: currentInputMessage, timestamp: new Date(), fileIds: messageFileIds, feedback: null, replyToMessageId: replyToId };
    const assistantMessage: Message = { id: `msg-${uuidv4()}`, role: "assistant", content: "", timestamp: new Date(Date.now() + 1), fileIds: [], feedback: null, isThinking: true };
    addOptimisticMessage(currentActiveChatId, userMessage);
    addOptimisticMessage(currentActiveChatId, assistantMessage);
    const filesInfoForApi: ApiFileInfo[] = filesAddedThisTurn.map(f => ({ id: f.id, type: f.type, url: f.url, name: f.name, size: f.size, extension: f.extension, created_at: f.created_at }));
    setInputMessage("");
    setSelectedFiles([]);
    const conversationId = initialChat.difyConversationId || undefined;
    // Immediately persist the new user message + empty assistant placeholder to the
    // message cache. If the page is refreshed mid-stream, the cache will contain the
    // empty assistant message, which triggers needsRecovery in useChatMessages and
    // causes fetchHistory to be called on reload — recovering both the user message
    // and Dify's completed answer.
    // Note: chatSessionsRef won't reflect addOptimisticMessage yet (async state), so
    // we build the message list directly from initialChat.messages.
    saveMessagesToCache(currentActiveChatId, conversationId, [...initialChat.messages, userMessage, assistantMessage]);

    // For the first message in a new conversation, append the connected platform name
    // so the AI knows which platform the user has connected (not shown to user).
    // Skip if the user already mentioned a platform in their message.
    const difyMessage = currentInputMessage;

    // eslint-disable-next-line no-console
    console.log('[sendMessage] sending to backend', {
      chatId: currentActiveChatId,
      hasConversationId: !!conversationId,
      conversationId,
      currentInputLength: currentInputMessage.length,
      filesCount: filesInfoForApi.length,
      connectedAccountCount: connectedAccountPayload?.accounts.length ?? 0,
    });
    try {
      const response = await chatApi.fetchDifyResponse(
        difyMessage,
        conversationId,
        userId,
        filesInfoForApi.length > 0 ? filesInfoForApi : undefined,
        connectedAccountPayload
      );
      if (response.error) {
        // Check if it's a rate limit error and handle it
        if (response.rateLimitError) {
          const handled = await handleRateLimitErrorFn(response.rateLimitError);
          if (handled) {
            // Rate limit dialog will be shown by useRateLimit hook
            updateOptimisticMessage(currentActiveChatId, assistantMessage.id, {
              content: `Error: ${response.error}`,
              isRateLimitError: true,
            });
            setIsResponding(false);
            setCurrentTaskId(null);
            return;
          }
        }

        // Check if it's a Dify error
        const isDify = isDifyError(response.error);
        const difyStatusCode = extractDifyErrorCode(response.error || '');

        if (isDify && difyStatusCode) {
          updateOptimisticMessage(currentActiveChatId, assistantMessage.id, {
            content: '',
            isDifyAgentError: true,
            errorMessage: response.error,
            errorCode: ERROR_CODES.CHAT.DIFY_AGENT_ERROR,
          });
        } else {
          updateOptimisticMessage(currentActiveChatId, assistantMessage.id, { content: `Error: ${response.error}` });
        }
        setIsResponding(false);
        setCurrentTaskId(null);
      } else if (response.responseBody) {
        await processStream(response.responseBody.getReader(), currentActiveChatId, assistantMessage.id, conversationId);
      }
    } catch (error: any) {
      // Check if it's a rate limit error
      if (error?.status === 429) {
          const handled = await handleRateLimitErrorFn(error);
        if (handled) {
          updateOptimisticMessage(currentActiveChatId, assistantMessage.id, {
            content: "Rate limit exceeded. Please upgrade your plan.",
            isRateLimitError: true,
          });
          setIsResponding(false);
          setCurrentTaskId(null);
          return;
        }
      }

      // Check if it's a Dify error
      const errorMessage = error?.message || String(error);
      const isDify = isDifyError(errorMessage);
      const difyStatusCode = extractDifyErrorCode(errorMessage);

      if (isDify && difyStatusCode) {
        updateOptimisticMessage(currentActiveChatId, assistantMessage.id, {
          content: '',
          isDifyAgentError: true,
          errorMessage: errorMessage,
          errorCode: ERROR_CODES.CHAT.DIFY_AGENT_ERROR,
        });
      } else {
        updateOptimisticMessage(currentActiveChatId, assistantMessage.id, { content: "A critical error occurred." });
      }
      setIsResponding(false);
      setCurrentTaskId(null);
    }
    } finally {
      // Ensure isResponding is reset on early returns or errors
      setIsResponding(false);
    }
  }, [inputMessage, selectedFiles, userId, activeChat, isResponding, replyingToMessage, createNewChat, cancelReply, updateChatSession, addOptimisticMessage, updateOptimisticMessage, setInputMessage, setSelectedFiles, chatApi.fetchDifyResponse, processStream, chatSessionsRef, connectedAccountPayload]);

  const stopStreaming = useCallback(async () => {
    // ... (same implementation as before)
    const taskIdToStop = currentTaskId;
    setIsResponding(false);
    setCurrentTaskId(null);
    if (taskIdToStop && taskIdToStop !== PENDING_TASK_ID && userId) {
      try {
        await chatApi.stopStreaming(taskIdToStop, userId);
      } catch (error) {
        console.error(`Error sending stop request for task ID ${taskIdToStop}:`, error);
      }
    }
  }, [currentTaskId, userId, chatApi.stopStreaming]);

  const handleRegenerate = useCallback(async (assistantMessageId: string) => {
    // ... (same implementation as before)
    if (!activeChat || isResponding || !userId) return;
    const assistantMessageIndex = activeChat.messages.findIndex(msg => msg.id === assistantMessageId);
    if (assistantMessageIndex < 1) return;
    let userMessage: Message | null = null;
    let userMessageIndex = -1;
    for (let i = assistantMessageIndex - 1; i >= 0; i--) {
      if (activeChat.messages[i].role === 'user') {
        userMessage = activeChat.messages[i];
        userMessageIndex = i;
        break;
      }
    }
    if (!userMessage) return;
    setIsResponding(true);
    setCurrentTaskId(PENDING_TASK_ID);
    cancelReply();
    removeMessagesAfterIndex(activeChat.id, userMessageIndex);
    const newAssistantMessageId = `msg-${uuidv4()}`;
    addOptimisticMessage(activeChat.id, { id: newAssistantMessageId, role: "assistant", content: "", timestamp: new Date(), fileIds: [], feedback: null, isThinking: true });
    const updatedChat = chatSessionsRef.current?.find(c => c.id === activeChat.id);
    const filesForApiPayload = (userMessage.fileIds || []).map(fileId => updatedChat?.files.find(f => f.id === fileId)).filter((file): file is ChatFile => !!file).map(f => ({ id: f.id, type: f.type, url: f.url, name: f.name, size: f.size, extension: f.extension, created_at: f.created_at }));
    try {
      const response = await chatApi.fetchDifyResponse(
        userMessage.content,
        activeChat.difyConversationId,
        userId,
        filesForApiPayload,
        connectedAccountPayload
      );
      if (response.error) {
        // Check if it's a rate limit error
        if (response.rateLimitError) {
          const handled = await handleRateLimitErrorFn(response.rateLimitError);
          if (handled) {
            updateOptimisticMessage(activeChat.id, newAssistantMessageId, {
              content: `Error: ${response.error}`,
              isRateLimitError: true,
            });
            setIsResponding(false);
            setCurrentTaskId(null);
            return;
          }
        }

        // Check if it's a Dify error
        const isDify = isDifyError(response.error);
        const difyStatusCode = extractDifyErrorCode(response.error || '');

        if (isDify && difyStatusCode) {
          updateOptimisticMessage(activeChat.id, newAssistantMessageId, {
            content: '',
            isDifyAgentError: true,
            errorMessage: response.error,
            errorCode: ERROR_CODES.CHAT.DIFY_AGENT_ERROR,
          });
          setIsResponding(false);
          setCurrentTaskId(null);
          return;
        }

        throw new Error(response.error);
      }
      if (response.responseBody) {
        await processStream(response.responseBody.getReader(), activeChat.id, newAssistantMessageId, activeChat.difyConversationId);
      }
    } catch (error: any) {
      // Check if it's a rate limit error
      if (error?.status === 429) {
          const handled = await handleRateLimitErrorFn(error);
        if (handled) {
          updateOptimisticMessage(activeChat.id, newAssistantMessageId, {
            content: "Rate limit exceeded. Please upgrade your plan.",
            isRateLimitError: true,
          });
          setIsResponding(false);
          setCurrentTaskId(null);
          return;
        }
      }

      // Check if it's a Dify error
      const errorMessage = error?.message || String(error);
      const isDify = isDifyError(errorMessage);
      const difyStatusCode = extractDifyErrorCode(errorMessage);

      if (isDify && difyStatusCode) {
        updateOptimisticMessage(activeChat.id, newAssistantMessageId, {
          content: '',
          isDifyAgentError: true,
          errorMessage: errorMessage,
          errorCode: ERROR_CODES.CHAT.DIFY_AGENT_ERROR,
        });
      } else {
        updateOptimisticMessage(activeChat.id, newAssistantMessageId, { content: "Error regenerating response." });
      }
      setIsResponding(false);
      setCurrentTaskId(null);
    }
  }, [activeChat, isResponding, userId, removeMessagesAfterIndex, addOptimisticMessage, chatApi, processStream, updateOptimisticMessage, cancelReply, chatSessionsRef, connectedAccountPayload]);

  const sendFeedback = useCallback(async (messageId: string, rating: 'like' | 'dislike') => {
    // ... (same implementation as before)
    if (!activeChat || !userId) return;
    const message = activeChat.messages.find(m => m.id === messageId);
    if (!message || !message.difyMessageId) return;
    updateOptimisticMessage(activeChat.id, messageId, { feedback: rating });
    try {
      await chatApi.postFeedback(message.difyMessageId, rating, userId);
      const finalChatState = chatSessionsRef.current?.find(c => c.id === activeChat.id);
      if (finalChatState) {
        saveMessagesToCache(activeChat.id, activeChat.difyConversationId, finalChatState.messages);
      }
    } catch (error) {
      updateOptimisticMessage(activeChat.id, messageId, { feedback: null });
    }
  }, [activeChat, userId, chatApi, updateOptimisticMessage, chatSessionsRef, saveMessagesToCache]);

  const handleSaveEditAction = useCallback(async (editedMessage: Message, newContent: string) => {
    if (!activeChat || isResponding || !userId) return;

    const editedMessageIndex = activeChat.messages.findIndex(msg => msg.id === editedMessage.id);
    if (editedMessageIndex === -1) return;

    setIsResponding(true);
    setCurrentTaskId(PENDING_TASK_ID);

    const updatedUserMessage: Message = { ...editedMessage, content: newContent, timestamp: new Date() };
    setChatSessions(prev =>
      prev.map(chat => {
        if (chat.id === activeChat.id) {
          const newMessages = [...chat.messages];
          newMessages[editedMessageIndex] = updatedUserMessage;
          const truncatedMessages = newMessages.slice(0, editedMessageIndex + 1);
          return { ...chat, messages: truncatedMessages, updatedAt: new Date() };
        }
        return chat;
      })
    );

    const assistantMessageId = `msg-${uuidv4()}`;
    const assistantMessage: Message = { id: assistantMessageId, role: "assistant", content: "", timestamp: new Date(Date.now() + 1), fileIds: [], feedback: null, isThinking: true };
    addOptimisticMessage(activeChat.id, assistantMessage);

    const updatedChat = chatSessionsRef.current?.find(c => c.id === activeChat.id);
    const filesForApiPayload = (updatedUserMessage.fileIds || []).map(fileId => updatedChat?.files.find(f => f.id === fileId)).filter((file): file is ChatFile => !!file).map(f => ({ id: f.id, type: f.type, url: f.url, name: f.name, size: f.size, extension: f.extension, created_at: f.created_at }));

    try {
      const response = await chatApi.fetchDifyResponse(
        newContent,
        activeChat.difyConversationId,
        userId,
        filesForApiPayload,
        connectedAccountPayload
      );
      if (response.error) {
        // Check if it's a rate limit error
        if (response.rateLimitError) {
          const handled = await handleRateLimitErrorFn(response.rateLimitError);
          if (handled) {
            updateOptimisticMessage(activeChat.id, assistantMessageId, {
              content: `Error: ${response.error}`,
              isRateLimitError: true,
            });
            setIsResponding(false);
            setCurrentTaskId(null);
            return;
          }
        }

        // Check if it's a Dify error
        const isDify = isDifyError(response.error);
        const difyStatusCode = extractDifyErrorCode(response.error || '');

        if (isDify && difyStatusCode) {
          updateOptimisticMessage(activeChat.id, assistantMessageId, {
            content: '',
            isDifyAgentError: true,
            errorMessage: response.error,
            errorCode: ERROR_CODES.CHAT.DIFY_AGENT_ERROR,
          });
          setIsResponding(false);
          setCurrentTaskId(null);
          return;
        }

        throw new Error(response.error);
      }
      if (response.responseBody) {
        await processStream(response.responseBody.getReader(), activeChat.id, assistantMessageId, activeChat.difyConversationId);
      }
    } catch (error: any) {
      console.error("Error resending after edit:", error);
      // Check if it's a rate limit error
      if (error?.status === 429) {
          const handled = await handleRateLimitErrorFn(error);
        if (handled) {
          updateOptimisticMessage(activeChat.id, assistantMessageId, {
            content: "Rate limit exceeded. Please upgrade your plan.",
            isRateLimitError: true,
          });
          setIsResponding(false);
          setCurrentTaskId(null);
          return;
        }
      }

      // Check if it's a Dify error
      const errorMessage = error?.message || String(error);
      const isDify = isDifyError(errorMessage);
      const difyStatusCode = extractDifyErrorCode(errorMessage);

      if (isDify && difyStatusCode) {
        updateOptimisticMessage(activeChat.id, assistantMessageId, {
          content: '',
          isDifyAgentError: true,
          errorMessage: errorMessage,
          errorCode: ERROR_CODES.CHAT.DIFY_AGENT_ERROR,
        });
      } else {
        updateOptimisticMessage(activeChat.id, assistantMessageId, { content: "Error resending message." });
      }
      setIsResponding(false);
      setCurrentTaskId(null);
    }
  }, [activeChat, isResponding, userId, setChatSessions, addOptimisticMessage, updateOptimisticMessage, chatApi, processStream, chatSessionsRef, connectedAccountPayload]);


  return {
    isResponding,
    currentTaskId,
    sendMessage,
    stopStreaming,
    handleRegenerate,
    sendFeedback,
    handleSaveEditAction,
    refreshConversationAfterError,
  };
}
